import { Navigate, Link, useParams } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import {
  Eyebrow,
  PrimaryLink,
  Reveal,
  FinalCTA,
  usePageMeta,
} from "../components/Common";
import { SERVICES } from "../data/content";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  usePageMeta(
    service ? `${service.title} — English for Leaders` : "Expertise — English for Leaders",
    service ? service.metaDesc || service.short : undefined,
    `/expertise/${slug}`
  );

  if (!service) return <Navigate to="/expertise" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid="service-detail-page">
      <section className="bg-navy" data-testid="service-hero">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-10 lg:py-24">
          <Reveal>
            <Eyebrow light>Expertise</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl tracking-tight text-white sm:text-5xl">
              {service.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              {service.audience}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-12 lg:px-10 lg:py-24">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-lg leading-relaxed text-charcoal/85 sm:text-xl">
                {service.intro}
              </p>
              <h2 className="mt-12 font-display text-2xl tracking-tight text-navy sm:text-3xl">
                How I can help
              </h2>
              <ul className="mt-6 space-y-4">
                {service.offerings.map((o) => (
                  <li
                    key={o}
                    className="flex items-start gap-4 border-b border-navy/10 pb-4 text-base leading-relaxed text-charcoal/85"
                  >
                    <Check className="mt-1 h-5 w-5 shrink-0 text-crimson" strokeWidth={2.5} />
                    {o}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <aside className="lg:col-span-4">
            <Reveal delay={0.1} className="lg:sticky lg:top-28">
              <div className="border border-navy/10 bg-offwhite p-8">
                <h3 className="font-display text-xl text-navy">Discuss this service</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                  A short conversation is usually enough to see whether the fit is
                  right. Every enquiry is answered personally.
                </p>
                <div className="mt-6">
                  <PrimaryLink
                    to={`/contact?interest=${service.slug}`}
                    testid="service-cta-button"
                  >
                    {service.ctaLabel}
                  </PrimaryLink>
                </div>
              </div>

              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-navy/55">
                  Other services
                </p>
                <ul className="mt-4 space-y-3">
                  {others.map((o) => (
                    <li key={o.slug}>
                      <Link
                        to={`/expertise/${o.slug}`}
                        className="text-sm font-semibold text-navy underline decoration-crimson/40 underline-offset-4 transition-colors hover:decoration-crimson"
                      >
                        {o.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/expertise"
                  data-testid="service-back-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-charcoal/60 transition-colors hover:text-navy"
                >
                  <ArrowLeft className="h-4 w-4" />
                  All expertise
                </Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default ServiceDetail;
